import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listDialogues,
  createDialogue,
  updateDialogue,
  deleteDialogue,
  type Dialogue,
} from "../api/dialogues";

export function DialoguesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["dialogues"], queryFn: listDialogues });
  const [editing, setEditing] = useState<Dialogue | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => deleteDialogue(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dialogues"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading dialogues…</p>;
  const rows = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">AI Dialogues</h1>
        <button
          onClick={() => setEditing("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New dialogue
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((d) => (
              <tr key={d.dialogue_id}>
                <td className="px-4 py-2 font-medium">{d.name}</td>
                <td className="px-4 py-2 text-gray-600">{d.description || "—"}</td>
                <td className="px-4 py-2 text-right space-x-3">
                  <button onClick={() => setEditing(d)} className="text-brand hover:underline">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete dialogue "${d.name}"?`)) remove.mutate(d.dialogue_id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-gray-400 text-sm p-4">No dialogues defined.</p>}
      </div>

      {editing && (
        <Editor
          dialogue={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["dialogues"] });
          }}
        />
      )}
    </div>
  );
}

function Editor({
  dialogue,
  onClose,
  onSaved,
}: {
  dialogue: Dialogue | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(dialogue?.name ?? "");
  const [description, setDescription] = useState(dialogue?.description ?? "");
  const [scriptBody, setScriptBody] = useState(dialogue?.script_body ?? "");
  const [data, setData] = useState(dialogue?.data ?? "");

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description,
        script_body: scriptBody,
        data,
      };
      return dialogue
        ? updateDialogue(dialogue.dialogue_id, body)
        : createDialogue(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
        <h2 className="text-lg font-bold">{dialogue ? "Edit" : "New"} dialogue</h2>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Script body</label>
          <textarea
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            rows={5}
            value={scriptBody}
            onChange={(e) => setScriptBody(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Data</label>
          <textarea
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            rows={4}
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
